import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { ProfileRead, ProfileWithHorseCount } from '../types/api';
import { apiService } from '../services/api';

interface ProfileEditModalProps {
  isOpen: boolean;
  profile: ProfileWithHorseCount | null; // null = create mode
  onClose: () => void;
  onSaved: (profile: ProfileRead) => void;
  onDeleted?: (profileId: string) => void;
}

export const ProfileEditModal: React.FC<ProfileEditModalProps> = ({
  isOpen,
  profile,
  onClose,
  onSaved,
  onDeleted,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName(profile?.name ?? '');
      setDescription(profile?.description ?? '');
      setError(null);
    }
  }, [isOpen, profile]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      let result: ProfileRead;
      if (profile) {
        result = await apiService.updateProfile(profile.id, {
          name: name.trim(),
          description: description.trim() || undefined,
        });
      } else {
        result = await apiService.createProfile({
          name: name.trim(),
          description: description.trim() || undefined,
        });
      }
      onSaved(result);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!profile) return;
    const confirmed = window.confirm(
      `Delete profile "${profile.name}"? This will remove it from all assigned horses.`
    );
    if (!confirmed) return;
    setSubmitting(true);
    try {
      await apiService.deleteProfile(profile.id);
      onDeleted?.(profile.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete profile');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Modal card */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#132B64]">
            {profile ? 'Edit Profile' : 'New Profile'}
          </h2>
          <button
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Name */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={100}
            placeholder="e.g. For Sale, Young Prospects…"
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#132B64]/30 focus:border-[#132B64]"
            autoFocus
          />
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Description <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="What is this profile for?"
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#132B64]/30 focus:border-[#132B64] resize-none"
          />
        </div>

        {/* Error */}
        {error && (
          <p className="text-xs text-red-600">{error}</p>
        )}

        {/* Footer */}
        <div className="flex items-center gap-2 pt-1">
          {profile && (
            <button
              onClick={handleDelete}
              disabled={submitting}
              className="px-4 py-2 rounded-full text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              Delete
            </button>
          )}
          <div className="flex-1" />
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-full text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={submitting || !name.trim()}
            className="px-5 py-2 rounded-full text-sm font-semibold text-white bg-[#132B64] hover:bg-[#0f2350] disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
